// hardcoded-ui-text: allow — each failure class carries a `messageKey` beside its English
// `message`, and WhatsAppErrorPanel.tsx resolves it. The English text left here is the
// fallback; the other two consumers use the class for retry and delivery state, not to render.
/**
 * WhatsApp Error Classifier
 *
 * Pure function: maps Meta Cloud API errors (and our own pre-flight errors) to
 * one of 7 typed categories. Drives V2 send hook retry policy and UI affordances.
 *
 * Categories:
 *  - window_closed     → 24h customer-initiated window expired (131047, 131049). Recoverable by sending a template.
 *  - rate_limit        → Meta throttling (code 4, 131048). Auto-retry with backoff.
 *  - permission        → Token / app permissions (#10, OAuthException). Admin must fix.
 *  - configuration     → WhatsApp not configured for tenant. Admin must fix.
 *  - invalid_recipient → Number not on WhatsApp / invalid phone (131056, 131026). Terminal.
 *  - transient         → Network / 5xx / timeout. Auto-retry.
 *  - unknown           → Anything else the six classes above don't match. ALWAYS carries a
 *                        written, keyed `message` — never the raw string a lower layer threw
 *                        (a Postgres error, a fetch client's own wrapper). That raw string is
 *                        kept as `debugDetail`, for support and the console, never for the
 *                        operator to read as the reason their message failed.
 *
 * `toClassifiableWhatsAppError()` below is the other half of this file: it unwraps a
 * `supabase.functions.invoke()` failure's real JSON body BEFORE it ever reaches
 * `classifyWhatsAppError`, so a 500 whose body names an actual reason (e.g. "WhatsApp API
 * configuration not found") lands in the `configuration` class instead of falling through
 * to `unknown` because all `classifyWhatsAppError` ever saw was the transport client's own
 * generic wrapper message.
 *
 * V1's `handleWhatsAppError` (src/hooks/useMessageSending.ts:457-511) currently
 * inlines this same logic with 5 categories. When V1 retires, it can adopt this
 * shared util as a 1-line import change.
 */

import { getEdgeFunctionErrorBody } from './error-utils';

export type WhatsAppFailureKind =
  | 'window_closed'
  | 'rate_limit'
  | 'permission'
  | 'configuration'
  | 'invalid_recipient'
  | 'transient'
  | 'unknown';

export interface WhatsAppFailureClass {
  kind: WhatsAppFailureKind;
  /** Whether the V2 send hook should auto-retry this error class. */
  autoRetryable: boolean;
  /** Human-friendly message for toasts and inline errors. English fallback. */
  message: string;
  /** i18n key for `message` (namespace `conversations`). Resolve it where the message is shown. */
  /**
   * REQUIRED, not optional, and the type is the candado. Every branch already sets it — but while
   * it was optional the panel had to write `error.messageKey ? t(...) : error.message`, and that
   * fallback is exactly the line that printed "edge-case: the project refused this send" at an
   * operator on 2026-09-08. With the field required the fallback has nothing to fall back to, so
   * the defect cannot be reintroduced by adding an eighth class and forgetting its key.
   */
  messageKey: string;
  /** Original error code if available, for logging/debugging. */
  code?: number | string;
  /**
   * Raw, unclassified detail (a transport wrapper string, a lower-layer error message) kept
   * for diagnosis ONLY — a title attribute, DEV console, a support ticket. Never rendered as
   * the message an operator reads; that is what `message`/`messageKey` are for. Only the
   * `unknown` class ever sets this — every other class already knows exactly what happened.
   */
  debugDetail?: string;
}

interface ErrorLike {
  code?: number | string;
  message?: string;
  status?: number;
  error?: ErrorLike;
}

function asErrorLike(err: unknown): ErrorLike {
  if (err && typeof err === 'object') return err as ErrorLike;
  if (typeof err === 'string') return { message: err };
  return {};
}

function getCode(e: ErrorLike): number | string | undefined {
  return e.code ?? e.error?.code ?? e.status;
}

function getMessage(e: ErrorLike): string {
  return e.message ?? e.error?.message ?? '';
}

export function classifyWhatsAppError(error: unknown): WhatsAppFailureClass {
  const e = asErrorLike(error);
  const code = getCode(e);
  const msg = getMessage(e);
  const lower = msg.toLowerCase();

  // 24h conversation window closed (re-engagement required)
  // Meta error codes: 131047 (re-engagement message), 131049 (conversation window closed)
  if (code === 131047 || code === 131049 || lower.includes('conversation window') || lower.includes('re-engagement')) {
    return {
      kind: 'window_closed',
      autoRetryable: false,
      message: 'The 24-hour conversation window is closed. Send a template to re-open it.', messageKey: 'sendErrors.windowClosed',
      code,
    };
  }

  // Rate limit / throttling
  // Meta error codes: 4 (App rate limit), 131048 (spam rate limit), 80007 (rate limit hit)
  if (code === 4 || code === 131048 || code === 80007 || lower.includes('rate limit') || lower.includes('too many requests')) {
    return {
      kind: 'rate_limit',
      autoRetryable: true,
      message: 'Sending temporarily rate-limited by WhatsApp. Will retry shortly.', messageKey: 'sendErrors.rateLimited',
      code,
    };
  }

  // Permission / token problems — admin must fix
  // Meta error: (#10) Application does not have permission, OAuthException, 190 (invalid access token)
  if (
    code === 10 || code === 190 ||
    lower.includes('(#10)') ||
    lower.includes('oauthexception') ||
    lower.includes('does not have permission') ||
    lower.includes('access token')
  ) {
    return {
      kind: 'permission',
      autoRetryable: false,
      message: "WhatsApp permission error. Your administrator needs to fix the integration.", messageKey: 'sendErrors.permission',
      code,
    };
  }

  // Configuration missing (our pre-flight checks before hitting Meta)
  if (
    lower.includes('whatsapp api configuration not found') ||
    lower.includes('whatsapp credentials not configured') ||
    lower.includes('whatsapp is not configured')
  ) {
    return {
      kind: 'configuration',
      autoRetryable: false,
      message: 'WhatsApp is not configured for this account. Contact your administrator.', messageKey: 'sendErrors.notConfigured',
      code,
    };
  }

  // Invalid recipient — number not on WhatsApp / invalid phone / blocked
  // Meta error codes: 131026 (message undeliverable), 131056 (recipient not on WhatsApp)
  if (code === 131026 || code === 131056 || lower.includes('not on whatsapp') || lower.includes('recipient phone number')) {
    return {
      kind: 'invalid_recipient',
      autoRetryable: false,
      message: "This number can't receive WhatsApp messages.", messageKey: 'sendErrors.cannotReceive',
      code,
    };
  }

  // Transient — network / 5xx / fetch failure / abort / timeout
  const numericCode = typeof code === 'number' ? code : Number(code);
  const is5xx = !Number.isNaN(numericCode) && numericCode >= 500 && numericCode < 600;
  if (
    is5xx ||
    lower.includes('network') ||
    lower.includes('fetch failed') ||
    lower.includes('failed to fetch') ||
    lower.includes('timeout') ||
    lower.includes('aborted') ||
    lower.includes('econnreset') ||
    lower.includes('socket')
  ) {
    return {
      kind: 'transient',
      autoRetryable: true,
      // The copy no longer promises a retry in progress. This class IS auto-retried, but a
      // WhatsAppFailureClass is only ever rendered once the mutation has already errored — that is,
      // after the retries are spent. "Retrying…" was therefore false at exactly the moment it was
      // on screen, and an operator who believed it walked away from a message that never went out.
      message: 'There was no connection, so this message did not go out. Retry it when you are back online.',
      messageKey: 'sendErrors.connection',
      code,
    };
  }

  // Unknown — surface to user with a written, translated sentence. `msg` may be empty
  // (nothing to say beyond the generic text) or it may be a raw string from a layer that
  // never expected a person to read it (a transport client's own wrapper, a Postgres
  // error) — either way `messageKey` is ALWAYS set, so WhatsAppErrorPanel never falls
  // back to rendering `msg` verbatim. `msg` itself is kept as `debugDetail` when present.
  return {
    kind: 'unknown',
    autoRetryable: false,
    message: 'The message could not be sent for an unrecognized reason. Try again, and contact your administrator if it keeps happening.',
    messageKey: 'sendErrors.generic',
    debugDetail: msg || undefined,
    code,
  };
}

/**
 * Normalizes a thrown `supabase.functions.invoke()` failure so `classifyWhatsAppError`
 * sees the edge function's real reason instead of the generic transport wrapper.
 *
 * A non-2xx response throws a `FunctionsHttpError` whose OWN `.message` is always
 * "Edge Function returned a non-2xx status code" — the actual reason (and, per this
 * platform's edge-function convention, an optional `code`) lives in the JSON body of
 * `.context`, which `getEdgeFunctionErrorBody()` reads. Call this ONCE, at the point
 * a V2 send path catches the invoke() error and is about to throw it onward — every
 * later reader of that same error (the mutation's `retry` callback, its `onError`
 * handler, `useMutationState`'s `select`) then classifies the already-unwrapped error
 * with the plain, synchronous `classifyWhatsAppError`, with no third copy of this
 * unwrapping and no need for any of those three sites to become async.
 *
 * Falls back to the original error untouched when there is nothing to unwrap (a
 * `FunctionsFetchError` with no HTTP response, a body that isn't the expected JSON
 * shape, or an error that was never a functions.invoke() failure at all) —
 * `classifyWhatsAppError` already has a path for that: the `unknown` class above.
 */
export async function toClassifiableWhatsAppError(error: unknown): Promise<unknown> {
  const body = await getEdgeFunctionErrorBody(error);
  if (!body || (body.error === undefined && body.code === undefined)) return error;
  const enriched = new Error(body.error ?? '') as Error & { code?: number | string };
  if (body.code !== undefined) enriched.code = body.code;
  return enriched;
}
